import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useInjectReducer } from 'utils/redux-injectors';
import { baseQuery, endpoints } from 'utils/api/endpoints';
import {
  State,
  TelegramBotConfig,
  TelegramBotConfigResponse,
  TelegramBotConfigSingleResponse,
  ModulesWithBotsResponse,
  ModuleWithBots,
  CreateBotConfigRequest,
  UpdateBotConfigRequest,
  TestBotRequest,
  TestBotResponse,
  AvailableChatsResponse,
  ModulesResponse,
} from './types';

export const initialState: State = {
  selectedBot: null,
};

const slice = createSlice({
  name: 'telegramBotConfig',
  initialState,
  reducers: {
    setSelectedBot: (
      state,
      action: PayloadAction<TelegramBotConfig | null>,
    ) => {
      state.selectedBot = action.payload;
    },
  },
});

export const api = createApi({
  reducerPath: 'telegramBotConfigApi',
  baseQuery: baseQuery,
  tagTypes: ['TelegramBotConfig'],
  endpoints: builder => ({
    // Get all bot configurations - supports both flat and grouped responses
    getBotConfigurations: builder.query<
      TelegramBotConfigResponse | ModulesWithBotsResponse,
      void
    >({
      query: () => ({
        url: endpoints.TELEGRAM_BOT_CONFIG,
        method: 'GET',
      }),
      transformResponse: (response: any) => {
        // Handle different response formats
        if (response.success !== false && response.data) {
          // Check if response is grouped (has modules with bots)
          const firstItem = response.data[0];
          if (
            firstItem &&
            'bots' in firstItem &&
            Array.isArray((firstItem as any).bots)
          ) {
            // It's a ModulesWithBotsResponse
            return {
              success: true,
              data: response.data as ModuleWithBots[],
              count: response.count || response.data.length,
            } as ModulesWithBotsResponse;
          }
          // Otherwise, it's a flat TelegramBotConfig[]
          return {
            success: true,
            data: response.data as TelegramBotConfig[],
            count: response.count || response.data.length,
          } as TelegramBotConfigResponse;
        }
        // If response is direct array
        if (Array.isArray(response)) {
          const firstItem = response[0];
          if (firstItem && 'bots' in firstItem) {
            return {
              success: true,
              data: response as ModuleWithBots[],
              count: response.length,
            } as ModulesWithBotsResponse;
          }
          return {
            success: true,
            data: response as TelegramBotConfig[],
            count: response.length,
          } as TelegramBotConfigResponse;
        }
        // If response has nested data
        if (response.data && Array.isArray(response.data)) {
          const firstItem = response.data[0];
          if (firstItem && 'bots' in firstItem) {
            return {
              success: true,
              data: response.data as ModuleWithBots[],
              count: response.data.length,
            } as ModulesWithBotsResponse;
          }
          return {
            success: true,
            data: response.data as TelegramBotConfig[],
            count: response.data.length,
          } as TelegramBotConfigResponse;
        }
        return {
          success: true,
          data: [],
          count: 0,
        } as TelegramBotConfigResponse;
      },
      providesTags: result => {
        if (!result?.data) {
          return [{ type: 'TelegramBotConfig', id: 'LIST' }];
        }
        // Check if it's grouped response or flat list
        const firstItem = (result.data as any[])[0];
        if (firstItem?.bots) {
          // Grouped response - extract all bot IDs
          const botIds: number[] = [];
          (result.data as ModuleWithBots[]).forEach(
            (module: ModuleWithBots) => {
              if (module.bots && Array.isArray(module.bots)) {
                module.bots.forEach((bot: TelegramBotConfig) => {
                  if (bot.id) botIds.push(bot.id);
                });
              }
            },
          );
          return [
            ...botIds.map(id => ({ type: 'TelegramBotConfig' as const, id })),
            { type: 'TelegramBotConfig', id: 'LIST' },
          ];
        } else {
          // Flat list
          return [
            ...(result.data as TelegramBotConfig[]).map(({ id }) => ({
              type: 'TelegramBotConfig' as const,
              id,
            })),
            { type: 'TelegramBotConfig', id: 'LIST' },
          ];
        }
      },
    }),

    // Get configuration by ID
    getBotConfigurationById: builder.query<
      TelegramBotConfigSingleResponse,
      number
    >({
      query: id => ({
        url: `${endpoints.TELEGRAM_BOT_CONFIG}/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: any) => {
        if (response.success) {
          return response;
        }
        return {
          success: true,
          data: response.data || response,
        };
      },
      providesTags: (_result, _error, id) => [
        { type: 'TelegramBotConfig', id },
      ],
    }),

    // Get configuration by module
    getBotConfigurationByModule: builder.query<
      TelegramBotConfigResponse,
      string
    >({
      query: module => ({
        url: `${endpoints.TELEGRAM_BOT_CONFIG}/module/${module}/bots`,
        method: 'GET',
      }),
      transformResponse: (response: any) => {
        if (response.success) {
          return {
            success: true,
            data: Array.isArray(response.data)
              ? response.data
              : response.data
                ? [response.data]
                : [],
          };
        }
        return {
          success: true,
          data: [],
        };
      },
      providesTags: (_result, _error, module) => [
        { type: 'TelegramBotConfig', id: `module-${module}` },
      ],
    }),

    // Create bot configuration
    createBotConfiguration: builder.mutation<
      TelegramBotConfigSingleResponse,
      CreateBotConfigRequest
    >({
      query: data => ({
        url: endpoints.TELEGRAM_BOT_CONFIG,
        method: 'POST',
        body: {
          ...(data.modules
            ? { modules: data.modules }
            : { module: data.module }),
          bot_token: data.bot_token,
          chat_id: data.chat_id || null,
          name: data.chat_name || null, // API expects 'name' not 'chat_name'
          bot_name: data.bot_name || null,
          description: data.description || null,
          is_active: data.is_active !== undefined ? data.is_active : true,
        },
      }),
      transformResponse: (response: any) => {
        if (response.success) {
          return response;
        }
        return {
          success: true,
          message:
            response.message ||
            'Telegram bot configuration created successfully',
          data: response.data || response,
        };
      },
      invalidatesTags: ['TelegramBotConfig'],
    }),

    // Update bot configuration
    updateBotConfiguration: builder.mutation<
      TelegramBotConfigSingleResponse,
      { id: number; data: UpdateBotConfigRequest }
    >({
      query: ({ id, data }) => {
        const body: any = {};
        if (data.module !== undefined) body.module = data.module;
        if (data.modules !== undefined) body.modules = data.modules;
        if (data.bot_token !== undefined) body.bot_token = data.bot_token;
        if (data.chat_id !== undefined) body.chat_id = data.chat_id;
        if (data.chat_name !== undefined) body.name = data.chat_name; // API expects 'name' not 'chat_name'
        if (data.bot_name !== undefined) body.bot_name = data.bot_name;
        if (data.description !== undefined) body.description = data.description;
        if (data.is_active !== undefined) body.is_active = data.is_active;

        return {
          url: `${endpoints.TELEGRAM_BOT_CONFIG}/${id}`,
          method: 'PUT',
          body,
        };
      },
      transformResponse: (response: any) => {
        if (response.success) {
          return response;
        }
        return {
          success: true,
          message:
            response.message ||
            'Telegram bot configuration updated successfully',
          data: response.data || response,
        };
      },
      invalidatesTags: (_result, _error, { id }) => [
        'TelegramBotConfig',
        { type: 'TelegramBotConfig', id },
      ],
    }),

    // Delete bot configuration
    deleteBotConfiguration: builder.mutation<
      { success: boolean; message: string },
      number
    >({
      query: id => ({
        url: `${endpoints.TELEGRAM_BOT_CONFIG}/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (response: any) => {
        if (response.success) {
          return response;
        }
        return {
          success: true,
          message:
            response.message ||
            'Telegram bot configuration deleted successfully',
        };
      },
      invalidatesTags: (_result, _error, id) => [
        'TelegramBotConfig',
        { type: 'TelegramBotConfig', id },
      ],
    }),

    // Test bot configuration
    testBotConfiguration: builder.mutation<
      TestBotResponse,
      { id: number; data?: TestBotRequest }
    >({
      query: ({ id, data }) => ({
        url: `${endpoints.TELEGRAM_BOT_CONFIG}/${id}/test`,
        method: 'POST',
        body: data || {},
      }),
      transformResponse: (response: any) => {
        if (response.success) {
          return response;
        }
        // Handle error responses
        if (response.error) {
          return {
            success: false,
            message: response.error || 'Failed to send test message',
            details: response.details,
          };
        }
        return {
          success: true,
          message: response.message || 'Test message sent successfully',
          data: response.data || response,
        };
      },
    }),

    // Get available chats
    getAvailableChats: builder.mutation<
      AvailableChatsResponse,
      { bot_token: string }
    >({
      query: ({ bot_token }) => ({
        url: `${endpoints.TELEGRAM_BOT_CONFIG}/available-chats`,
        method: 'POST',
        body: { bot_token },
      }),
      transformResponse: (response: any) => {
        if (response.success) {
          return response;
        }
        // Handle error responses
        if (response.error) {
          return {
            success: false,
            data: [],
            count: 0,
            message: response.error,
            warning: response.warning,
          };
        }
        // If response is array
        if (Array.isArray(response)) {
          return {
            success: true,
            data: response,
            count: response.length,
            message: `Found ${response.length} chat(s)`,
          };
        }
        // If response has nested data
        if (response.data && Array.isArray(response.data)) {
          return {
            success: true,
            data: response.data,
            count: response.data.length,
            message:
              response.message || `Found ${response.data.length} chat(s)`,
            warning: response.warning,
          };
        }
        return {
          success: true,
          data: [],
          count: 0,
          message: response.message || 'No chats found',
          warning: response.warning,
        };
      },
    }),

    // Verify bot token (using getMe endpoint internally or available-chats)
    verifyBotToken: builder.mutation<
      {
        success: boolean;
        data?: {
          username: string;
          name: string;
          bot_username?: string;
          bot_name?: string;
        };
        error?: string;
      },
      { bot_token: string }
    >({
      query: ({ bot_token }) => ({
        url: `${endpoints.TELEGRAM_BOT_CONFIG}/verify-token`,
        method: 'POST',
        body: { bot_token },
      }),
      transformResponse: (response: any) => {
        if (response.success && response.data) {
          return {
            success: true,
            data: {
              username:
                response.data.username || response.data.bot_username || '@Bot',
              name:
                response.data.name || response.data.bot_name || 'Telegram Bot',
              bot_username:
                response.data.bot_username || response.data.username || '@Bot',
              bot_name:
                response.data.bot_name || response.data.name || 'Telegram Bot',
            },
          };
        }
        return {
          success: false,
          error: response.error || 'Invalid bot token',
        };
      },
      transformErrorResponse: (response: any) => {
        return {
          success: false,
          error: response?.data?.error || 'Failed to verify bot token',
        };
      },
    }),

    // Get all modules (hierarchical structure)
    getModules: builder.query<ModulesResponse, void>({
      query: () => ({
        url: endpoints.TELEGRAM_BOT_CONFIG_MODULES,
        method: 'GET',
      }),
      transformResponse: (response: any) => {
        if (
          response.success !== false &&
          response.data &&
          Array.isArray(response.data)
        ) {
          // Check if response has children (hierarchical) or is flat
          const firstItem = response.data[0];
          const hasChildren =
            firstItem &&
            'children' in firstItem &&
            Array.isArray(firstItem.children);

          if (hasChildren) {
            // Already hierarchical structure with children
            return {
              success: true,
              data: response.data,
              flat: response.flat || [],
              count: response.count,
            };
          } else {
            // Flat list - these are the actual modules to select
            // Convert to ModuleCategory format for display
            const categories: ModuleCategory[] = response.data.map(
              (item: any) => ({
                id: item.module_id || item.id,
                module_id: item.module_id || item.id,
                module_key: item.module_key,
                module_name: item.module_name,
                category: item.category || item.module_key,
                description: item.description || '',
                children: [], // Empty children - these are the modules themselves
                active_bots: item.active_bots,
                total_bots: item.total_bots,
                status: item.status,
                sort_order: item.sort_order,
              }),
            );

            return {
              success: true,
              data: categories,
              flat: response.data,
              count: response.count,
            };
          }
        }
        return {
          success: true,
          data: [],
          flat: [],
          count: 0,
        };
      },
    }),

    // Update bot's module subscriptions
    updateBotModules: builder.mutation<
      TelegramBotConfigSingleResponse,
      { id: number; modules: string[] }
    >({
      query: ({ id, modules }) => ({
        url: `${endpoints.TELEGRAM_BOT_CONFIG}/${id}/modules`,
        method: 'PUT',
        body: { modules },
      }),
      transformResponse: (response: any) => {
        if (response.success) {
          return response;
        }
        return {
          success: true,
          message:
            response.message || 'Bot module subscriptions updated successfully',
          data: response.data || response,
        };
      },
      invalidatesTags: (_result, _error, { id }) => [
        'TelegramBotConfig',
        { type: 'TelegramBotConfig', id },
      ],
    }),

    // Search bots with pagination
    searchBots: builder.query<
      import('./types').BotsSearchResponse,
      import('./types').BotsSearchParams
    >({
      query: ({ search = '', page = 1, limit = 20 }) => {
        const params = new URLSearchParams();
        if (search) params.append('search', search);
        params.append('page', String(page));
        params.append('limit', String(limit));

        return {
          url: `${endpoints.TELEGRAM_BOT_CONFIG_BOTS}?${params.toString()}`,
          method: 'GET',
        };
      },
      transformResponse: (response: any) => {
        if (response.success !== false && response.data) {
          return {
            success: true,
            data: Array.isArray(response.data) ? response.data : [],
            count: response.count || 0,
            page: response.page || 1,
            limit: response.limit || 20,
            total: response.total || response.count || 0,
          };
        }
        return {
          success: true,
          data: [],
          count: 0,
          page: 1,
          limit: 20,
          total: 0,
        };
      },
      providesTags: result => {
        if (!result?.data) {
          return [{ type: 'TelegramBotConfig', id: 'SEARCH' }];
        }
        return [
          ...result.data.map(({ id }) => ({
            type: 'TelegramBotConfig' as const,
            id,
          })),
          { type: 'TelegramBotConfig', id: 'SEARCH' },
        ];
      },
    }),
  }),
});

export const { actions: telegramBotConfigActions } = slice;

export const useTelegramBotConfigSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });
  return {
    // Queries
    useGetBotConfigurationsQuery: api.useLazyGetBotConfigurationsQuery,
    useGetBotConfigurationByIdQuery: api.useLazyGetBotConfigurationByIdQuery,
    useGetBotConfigurationByModuleQuery:
      api.useLazyGetBotConfigurationByModuleQuery,
    useGetModulesQuery: api.useLazyGetModulesQuery,
    useSearchBotsQuery: api.useLazySearchBotsQuery,

    // Mutations
    useCreateBotConfigurationMutation: api.useCreateBotConfigurationMutation,
    useUpdateBotConfigurationMutation: api.useUpdateBotConfigurationMutation,
    useDeleteBotConfigurationMutation: api.useDeleteBotConfigurationMutation,
    useTestBotConfigurationMutation: api.useTestBotConfigurationMutation,
    useGetAvailableChatsMutation: api.useGetAvailableChatsMutation,
    useVerifyBotTokenMutation: api.useVerifyBotTokenMutation,
    useUpdateBotModulesMutation: api.useUpdateBotModulesMutation,
  };
};
